import React from 'react';
import { Formik } from 'formik';
import { merge } from 'lodash';
import * as Yup from 'yup';

import type {
  CreateScheduledReportExportRequest,
  CreateScheduledReportExportRequestGcsConfig,
  CreateScheduledReportExportRequestS3Config,
  CreateScheduledReportExportRequestSftpConfig,
  UpdateScheduledReportExportRequest,
} from '~/server_shared/openapi/data-contracts';
import { DATA_EXPORT } from '~/Types';
import { reportAxiosError } from '~/Utils';

import { ReportTemplatesProvider, useReportTemplates } from '../ReportTemplates/hooks/useReportTemplates';
import type { ReportParameters } from '../shared/ReportParameterFields';
import { createParametersValidation, stripEmptyParameters } from '../shared/ReportParameterFields';
import { SHARED_EXPORT_TEXTS } from '../shared/texts';

import type { ScheduledReportExport } from './hooks/useScheduledReportExports';
import type { DayOfWeek } from './dayOfWeek';
import ScheduledReportExportDialogInner from './ScheduledReportExportDialogInner';
import { SUBSCRIPTION_RULES_TEXTS } from './texts';

const DEFAULT_SECRET_TEMPLATE = '*****';
const SECRET_JSON_FIELDS = [
  'type',
  'project_id',
  'private_key_id',
  'private_key',
  'client_email',
  'client_id',
  'auth_uri',
  'token_uri',
  'auth_provider_x509_cert_url',
  'client_x509_cert_url',
];

interface ScheduledReportExportDialogProps {
  onClose: () => void;
  onSubmit: (values: CreateScheduledReportExportRequest | UpdateScheduledReportExportRequest) => Promise<unknown>;
  schedules: ScheduledReportExport[];
  scheduleToEdit?: ScheduledReportExport | null;
}

export interface FormValues {
  report_template_id: number | '';
  display_name: string;
  target_type: CreateScheduledReportExportRequest['target_type'];
  user_id?: string;
  recurrence_type: 'daily' | 'monthly';
  export_hour: string;
  end_date?: string;
  is_end_date: boolean;
  is_active: boolean;
  export_days: DayOfWeek[];
  day_of_month?: number;
  parameters: ReportParameters;
  target_configurations: {
    s3_bucket: CreateScheduledReportExportRequestS3Config;
    gcs_bucket: CreateScheduledReportExportRequestGcsConfig;
    sftp: CreateScheduledReportExportRequestSftpConfig;
  };
}

const ScheduledReportExportDialog: React.FC<ScheduledReportExportDialogProps> = (props) => (
  <ReportTemplatesProvider>
    <ScheduledReportExportDialogContent {...props} />
  </ReportTemplatesProvider>
);

const ScheduledReportExportDialogContent: React.FC<ScheduledReportExportDialogProps> = ({
  onClose,
  onSubmit,
  schedules,
  scheduleToEdit,
}) => {
  const { templates } = useReportTemplates();

  const defaultInitialValues: FormValues = {
    report_template_id: '',
    display_name: '',
    target_type: 'email',
    user_id: undefined,
    recurrence_type: 'daily',
    export_hour: '',
    end_date: undefined,
    is_end_date: false,
    is_active: true,
    export_days: [],
    day_of_month: undefined,
    parameters: {},
    target_configurations: {
      s3_bucket: {
        path: '',
        bucket_name: '',
        access_key_id: '',
        access_key_secret: '',
      },
      gcs_bucket: {
        path: '',
        bucket_name: '',
        secret_json: '',
      },
      sftp: {
        host: '',
        port: '',
        path: '',
        user: '',
        password: '',
      },
    },
  };

  const serverExportHourToClient = (serverExportHour: string | null) =>
    serverExportHour ? serverExportHour.substring(0, 5) : '';

  let initialValues = defaultInitialValues;
  if (scheduleToEdit) {
    initialValues = merge({}, defaultInitialValues, scheduleToEdit);
    initialValues.export_hour = serverExportHourToClient(scheduleToEdit.export_hour ?? null);
    initialValues.is_end_date = !!scheduleToEdit.end_date;
  }

  const getDisplayNameValidation = (displayName: string | undefined) => {
    if (!displayName) {
      return true;
    }

    const displayNameExists = schedules.map((item) => item.display_name).includes(displayName);

    if (scheduleToEdit) {
      return scheduleToEdit.display_name === displayName || !displayNameExists;
    }
    return !displayNameExists;
  };

  const getSecretJsonValidation = (secretJsonStr: string | undefined) => {
    try {
      if (!secretJsonStr || secretJsonStr === DEFAULT_SECRET_TEMPLATE) {
        return true;
      }

      const secretJsonKeys = Object.keys(JSON.parse(secretJsonStr));
      return SECRET_JSON_FIELDS.every((field) => secretJsonKeys.includes(field));
    } catch {
      return false;
    }
  };

  const validateNoLeadingTrailingWhitespace = (value: unknown) => {
    if (!value || typeof value !== 'string') {
      return true;
    }
    return value === value.trim();
  };

  const noWhitespaceString = Yup.string().test(
    'no_whitespace',
    SHARED_EXPORT_TEXTS.VALIDATION_NO_WHITESPACE,
    validateNoLeadingTrailingWhitespace
  );
  const noWhitespaceRequiredString = noWhitespaceString.required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED);

  const validationSchema = Yup.object().shape({
    report_template_id: Yup.number()
      .required(SUBSCRIPTION_RULES_TEXTS.REPORT_TEMPLATE_REQUIRED)
      .positive(SUBSCRIPTION_RULES_TEXTS.REPORT_TEMPLATE_REQUIRED),
    display_name: Yup.string()
      .required(SUBSCRIPTION_RULES_TEXTS.SUBSCRIPTION_NAME_REQUIRED)
      .test('is_display_name_unique', SUBSCRIPTION_RULES_TEXTS.SUBSCRIPTION_NAME_UNIQUE, getDisplayNameValidation),
    target_type: Yup.string()
      .oneOf(Object.keys(DATA_EXPORT.TARGET_TYPE))
      .required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED),
    user_id: Yup.string()
      .nullable()
      .when('target_type', { is: 'email', then: Yup.string().required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED) }),
    target_configurations: Yup.object()
      .when('target_type', {
        is: 's3_bucket',
        then: Yup.object().shape({
          s3_bucket: Yup.object().shape({
            path: noWhitespaceRequiredString,
            bucket_name: noWhitespaceRequiredString,
            access_key_id: noWhitespaceRequiredString,
            access_key_secret: noWhitespaceRequiredString,
          }),
        }),
      })
      .when('target_type', {
        is: 'gcs_bucket',
        then: Yup.object().shape({
          gcs_bucket: Yup.object().shape({
            path: noWhitespaceRequiredString,
            bucket_name: noWhitespaceRequiredString,
            secret_json: noWhitespaceString.test(
              'is_secret_json_valid',
              SHARED_EXPORT_TEXTS.VALIDATION_INVALID_SECRET_JSON,
              getSecretJsonValidation
            ),
          }),
        }),
      })
      .when('target_type', {
        is: 'sftp',
        then: Yup.object().shape({
          sftp: Yup.object().shape({
            host: noWhitespaceRequiredString,
            port: noWhitespaceRequiredString,
            path: noWhitespaceRequiredString,
            user: noWhitespaceRequiredString,
            password: noWhitespaceRequiredString,
          }),
        }),
      }),
    recurrence_type: Yup.string().oneOf(['daily', 'monthly']).required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED),
    export_days: Yup.array().when('recurrence_type', {
      is: 'daily',
      then: Yup.array()
        .required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED)
        .min(1, SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED),
      otherwise: Yup.array().notRequired(),
    }),
    day_of_month: Yup.number().when('recurrence_type', {
      is: 'monthly',
      then: Yup.number()
        .required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED)
        .integer(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED)
        .min(1, SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED)
        .max(28, SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED),
      otherwise: Yup.number().nullable().notRequired(),
    }),
    export_hour: Yup.string().required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED),
    end_date: Yup.string()
      .nullable()
      .when('is_end_date', { is: true, then: Yup.string().required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED) }),
    is_active: Yup.boolean().required(SHARED_EXPORT_TEXTS.VALIDATION_REQUIRED),
    parameters: createParametersValidation(templates),
  });

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const target_type = values.target_type;
      const isMonthly = values.recurrence_type === 'monthly';
      const submissionValues: CreateScheduledReportExportRequest = {
        report_template_id: values.report_template_id as number,
        display_name: values.display_name,
        target_type,
        recurrence_type: values.recurrence_type,
        export_hour: values.export_hour,
        is_active: values.is_active,
      };
      if (isMonthly) {
        submissionValues.day_of_month = values.day_of_month;
      } else {
        submissionValues.export_days = [...values.export_days].sort();
      }

      if (target_type === 'email') {
        submissionValues.user_id = values.user_id;
        submissionValues.target_configurations = {};
      } else {
        const chosenConfiguration = values.target_configurations[target_type];
        submissionValues.target_configurations = {
          [target_type]: chosenConfiguration,
        };
      }

      submissionValues.end_date = values.is_end_date && values.end_date ? values.end_date : null;
      submissionValues.parameters = stripEmptyParameters(values.parameters ?? {});

      await onSubmit(submissionValues);
      onClose();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      <ScheduledReportExportDialogInner onClose={onClose} isEdit={!!scheduleToEdit} />
    </Formik>
  );
};

export default ScheduledReportExportDialog;
